import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ScrollText, RefreshCw, Search } from "lucide-react";

/**
 * AdminAccountCodeAudit
 * Stage 7 observability — shows per-stage account-code matching attempts
 * (search / price / av-check / book / ur_modify / ticket) with hit/miss
 * status, airline, matched code, booking id and host error if any.
 */
const STAGE_COLOR = {
  search:    "bg-sky-50 text-sky-700",
  price:     "bg-indigo-50 text-indigo-700",
  av_check:  "bg-violet-50 text-violet-700",
  book:      "bg-amber-50 text-amber-700",
  ur_modify: "bg-emerald-50 text-emerald-700",
  ticket:    "bg-rose-50 text-rose-700",
};

export default function AdminAccountCodeAudit() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/account-code-audit");
      setRows(data.items || []);
      setStats(data.stats || {});
    } catch (_e) { toast.error("Failed to load audit log"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = (q || "").trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.airline_code, r.matched_account_code_id, r.matched_tour_code_id, r.booking_id, r.error_message, r.stage]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [rows, q]);

  return (
    <div className="p-6 space-y-4" data-testid="admin-account-code-audit">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[var(--brand-accent)] flex items-center gap-2">
            <ScrollText className="w-5 h-5 text-[var(--brand-primary)]" /> Account Code Audit
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl leading-relaxed">
            End-to-end Travelport (1G) hit log for every <strong>search · price · book · UR-modify · ticket</strong> attempt where a negotiated account or tour code was evaluated. Use this to diagnose missed matches and tune Booking/Travel windows.
          </p>
        </div>
        <button onClick={load} disabled={busy} data-testid="audit-refresh-btn"
                className="rounded-full px-4 py-2 text-sm font-semibold bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-dark)] flex items-center gap-2">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="audit-stats">
        {["search", "price", "av_check", "book", "ur_modify", "ticket"].map((s) => {
          const blob = stats[s] || { hits: 0, total: 0 };
          const rate = blob.total ? Math.round((100 * blob.hits) / blob.total) : 0;
          return (
            <Card key={s} className="p-3" data-testid={`audit-stat-${s}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase font-bold tracking-wide text-slate-500">{s.replace("_", " ")}</div>
                <Badge className={`${STAGE_COLOR[s] || "bg-slate-100 text-slate-600"} border-0 text-[10px]`}>{rate}%</Badge>
              </div>
              <div className="font-heading text-xl font-bold text-[var(--brand-accent)] mt-1">{blob.hits}/{blob.total}</div>
              <div className="text-[10px] text-slate-400">hits / attempts</div>
            </Card>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by airline / code / booking / error" className="pl-9" />
        </div>
        <div className="text-xs text-slate-500">{filtered.length} of {rows.length}</div>
      </div>

      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Time</TableHead>
            <TableHead>Stage</TableHead>
            <TableHead>Airline</TableHead>
            <TableHead>Account Code</TableHead>
            <TableHead>Tour Code</TableHead>
            <TableHead>Booking</TableHead>
            <TableHead>OK</TableHead>
            <TableHead>Error</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((r, i) => (
              <TableRow key={i} data-testid={`audit-row-${i}`}>
                <TableCell className="text-xs whitespace-nowrap text-slate-500">{(r.ts || "").replace("T", " ").slice(0, 19)}</TableCell>
                <TableCell><Badge className={`${STAGE_COLOR[r.stage] || "bg-slate-100"} border-0 text-[10px]`}>{r.stage}</Badge></TableCell>
                <TableCell className="font-mono font-bold text-[var(--brand-accent)]">{r.airline_code || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{r.matched_account_code_id || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{r.matched_tour_code_id || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{r.booking_id ? r.booking_id.slice(0, 8) : "—"}</TableCell>
                <TableCell>{r.success ? <span className="text-emerald-700 text-xs font-semibold">✓</span> : <span className="text-rose-700 text-xs font-semibold">✗</span>}</TableCell>
                <TableCell className="text-xs text-rose-600 max-w-md truncate" title={r.error_message || ""}>{r.error_message || "—"}</TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">No audit entries yet. Run a flight search after configuring an active Airline Account Code.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
